import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useEurRate from "../../hooks/useEurRate";
import { PRICE_TABLE as PRICES, toEur } from "../../config/prices.mjs";
import { linkTo } from "../../i18n/lang.mjs";

/* ── Price group → service page ───────────────
   /pricing is the second most visible page on the site and used to be a dead
   end: a visitor read a number and had nowhere to click to find out what it
   buys. Each group now carries one discreet link to the page that explains
   that service.

   Keyed "section-group", matching the indexes of `pricing.sections` in the
   locale files (and therefore PRICE_TABLE). Adding or reordering a group
   there means updating this map.

   `to` is the LOGICAL route; linkTo() turns it into the active language's
   slug and <Link> prepends the /en, /de basename, so /de/pricing links to
   /de/haare-faerben. */
const GROUP_LINKS = {
  // Frizer
  "0-2": { to: "/farbanje-kose", label: "hairColouring" }, // Farbanje + feniranje
  "0-3": { to: "/farbanje-kose", label: "hairColouring" }, // Farbanje cele kose
  "0-4": { to: "/farbanje-kose", label: "hairColouring" }, // Preliv + feniranje
  "0-5": { to: "/farbanje-kose", label: "hairColouring" }, // Blajhanje izrastka
  "0-6": { to: "/farbanje-kose", label: "hairColouring" }, // Blajhanje cele kose
  "0-7": { to: "/balayage", label: "balayage" }, // Balayage / Air Touch / Pramenovi
  "0-13": { to: "/tretmani-kose", label: "hairTreatments" }, // Tretmani kose
  "0-14": { to: "/musko-sisanje", label: "mensHaircut" }, // Muško šišanje
  // Trajna šminka
  "1-0": { to: "/permanent-makeup", label: "permanentMakeup" }, // Vega obrve
  "1-1": { to: "/permanent-makeup", label: "permanentMakeup" }, // BabyLips
  // Svilene trepavice
  "2-0": { to: "/eyelashes", label: "eyelashes" },
  "2-1": { to: "/eyelashes", label: "eyelashes" }, // Korekcija trepavica
  // Profesionalna šminka
  "4-0": { to: "/makeup", label: "makeup" },
  // Nokti
  "5-0": { to: "/nails", label: "nails" }, // Osnovne usluge manikira
  "5-1": { to: "/nails", label: "nails" }, // Nadogradnja - izlivanje
  // Pedikir
  "6-0": { to: "/nails", label: "nails" },
  // Depilacija
  "7-0": { to: "/depilation", label: "depilation" }, // Gornji deo tela
  "7-1": { to: "/depilation", label: "depilation" }, // Donji deo tela
};

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const sections = t("pricing.sections", { returnObjects: true });
  const eurRate = useEurRate();
  const lang = ["sr", "en", "de"].includes(i18n.language) ? i18n.language : "sr";

  return (
    <div className="container">
      <div className="vb-pricelist">
        <div className="vb-pricelist-head">
          <h2 className="vb-pricelist-title">{t("pricing.heading")}</h2>
          {eurRate && (
            <p className="vb-pricelist-eur-note">{t("pricing.eurNote")}</p>
          )}
        </div>

        {sections.map((section, si) => (
          <section className="vb-price-section" key={si}>
            <h2 className="vb-price-section-title">{section.title}</h2>

            {section.groups.map((group, gi) => {
              const more = GROUP_LINKS[`${si}-${gi}`];

              return (
                <div className="vb-price-group" key={gi}>
                  {group.subtitle && (
                    <h3 className="vb-price-subtitle">{group.subtitle}</h3>
                  )}

                  {eurRate && (
                    <div className="vb-price-columns">
                      <span className="vb-price-column-rsd">RSD</span>
                      <span className="vb-price-column-eur">≈ EUR</span>
                    </div>
                  )}

                  <ul className="vb-price-rows">
                    {group.items.map((name, ii) => (
                      <li className="vb-price-row" key={ii}>
                        <span className="vb-price-name">{name}</span>
                        <span className="vb-price-dots" aria-hidden="true" />
                        <span className="vb-price-value">
                          {PRICES[si][gi][ii]}
                        </span>
                        {eurRate && (
                          <span className="vb-price-value-eur">
                            {toEur(PRICES[si][gi][ii], eurRate)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>

                  {group.note && <p className="vb-price-note">{group.note}</p>}

                  {/* Deliberately a link and not a button: the price list is
                      still the page, this only offers a way out of it. */}
                  {more && (
                    <p className="vb-price-more">
                      <Link to={linkTo(more.to, lang)}>
                        {t(`pricing.links.${more.label}`)} <span aria-hidden="true">→</span>
                      </Link>
                    </p>
                  )}
                </div>
              );
            })}
          </section>
        ))}

        <p className="vb-price-footnote">{t("pricing.bottomNote")}</p>
      </div>
    </div>
  );
};

export default Pricing;
